<template>

    <popup-modal
        @close="$emit('close')"
        position="center"
    >
        <div class="w-960">

            <h1 class="mb-16 text-heading-1 font-semibold first-letter:uppercase">
                {{ fields.title }}
            </h1>

            <label class="block mb-32 text-primary-80 first-letter:uppercase">
                {{ fields.description }}
            </label>

            <div v-if="fetchingUsers" class="pt-16">
                <loading></loading>
            </div>

            <div v-else class="border border-primary-20 rounded">
                <div
                    v-for="user in users"
                    :key="'user-' + user.id"
                    class="flex items-center p-8 odd:bg-primary-5 even:bg-white first-of-type:rounded-t last-of-type:rounded-b"
                >
                    <div class="font-semibold text-body first-letter:uppercase mr-auto truncate flex items-center">
                        <avatar
                            :image="user.image_url"
                            size="small"
                            class="cursor-default border border-white mr-6"
                        >{{ user.initials }}
                        </avatar>
                        {{ user.name }}
                    </div>
                    <div class="mr-16" v-if="isJob && $can('jobs.manage') && $can('applications.manage')">
                        <tooltip :content="$t('permissions.mark_responsible')">
                            <checkbox :checked="user.id === target.responsible_user_id"
                                      @change="updateResponsible(user.id, $event)"/>
                        </tooltip>
                    </div>
                    <segmented-button
                        :id="'permission-level-' + user.id"
                        :value="getSegmentedButtonValue(user)"
                        :options="getSegmentedButtonOptions(user)"
                        @input="setLevel(user, $event)"
                    />
                    <div
                        class="ml-8 cursor-pointer"
                        @click="isSubscribed(user.id) ? unsubscribeUser(isSubscribed(user.id)) : subscribeUser(user)"
                    >
                        <svg-icon
                            :class="isSubscribed(user.id) ? 'text-secondary hover:text-secondary-80' : 'text-primary-20 hover:text-secondary'"
                            size="20"
                            :type="isSubscribed(user.id) ? 'notifications-filled' : 'notifications'"
                        />
                    </div>
                </div>
            </div>
        </div>
    </popup-modal>

</template>

<script>
import PopupModal from './PopupModal.vue';
import Application from '../../models/Application';
import Job from '../../models/Job';
import User from '../../models/User';
import Avatar from './Avatar.vue';
import SimpleButton from './SimpleButton.vue';
import Loading from './Loading.vue';
import * as permissions from '../../services/PermissionService';
import {uniq as _uniq} from 'lodash'
import SvgIcon from "./SvgIcon";
import Subscription from "../../models/Subscription";
import * as subscriptions from "../../services/SubscriptionService";
import {normalizeJson} from "../../../shared/helpers";
import Checkbox from "../forms/Checkbox";
import Tooltip from "../navigation/Tooltip";
import * as jobs from '../../services/JobService';
import SegmentedButton from "./SegmentedButton.vue";
import {mapStores} from "pinia";
import {useAuthenticationStore} from "../../state/modules/authentication";
import {useUsersStore} from "../../state/modules/users";
import {useRepo} from "pinia-orm";

export default {
    components: {
        SegmentedButton,
        Tooltip,
        Checkbox,
        SvgIcon,
        'popup-modal': PopupModal,
        'avatar': Avatar,
        'simple-button': SimpleButton,
        'loading': Loading,
    },
    props: {
        resourceTitle: {
            required: true,
            type: String,
        },
        target: {
            required: false,
            validator: value => value instanceof Application || value instanceof Job,
        },
        translations: Object
    },
    data() {
        return {
            updatingNotificationsOf: {},
            segmentedButtonColors: {
                active: {
                    2: 'bg-semantic-info text-white border-semantic-info',
                    1: 'bg-semantic-warning text-white border-semantic-warning',
                    0: 'bg-semantic-error text-white border-semantic-error',
                },
                inactive: 'bg-white border-primary-40 text-primary-40',
                disabled: 'opacity-50 cursor-not-allowed',
            }
        }
    },
    computed: {
        ...mapStores(useAuthenticationStore, useUsersStore),
        currentCompanyId() {
            return this.authenticationStore.currentCompanyId;
        },
        fetchingUsers() {
            return this.usersModuleStore.fetchingUsers;
        },
        permissionPrefix() {
            if (this.target instanceof Application)
                return "applications";
            if (this.target instanceof Job)
                return "jobs";
        },
        users() {
            return useRepo(User)
                .whereHas('companies', query => query.whereId(this.currentCompanyId))
                .with('companies')
                .get()
                .map(user => {
                    const perms = user.permissions[this.currentCompanyId] || {};
                    user.can = {};

                    if (perms.includes(this.permissionPrefix + '.manage'))
                        user.can.manage = true;
                    if (perms.includes(this.permissionPrefix + '.view'))
                        user.can.view = true;
                    if (perms.includes(this.permissionPrefix + '.manage.' + this.target.id))
                        user.can.manage_this = true;
                    if (perms.includes(this.permissionPrefix + '.view.' + this.target.id))
                        user.can.view_this = true;

                    if (this.target.stage?.job) {
                        const jobId = this.target.stage.job.id;

                        if (perms.includes('job-applications.manage.' + jobId))
                            user.can.manage_job = true;
                        if (perms.includes('job-applications.view.' + jobId))
                            user.can.view_job = true;
                    }

                    user.permLevel = 0;
                    if (user.can.manage || user.can.manage_this || user.can.manage_job)
                        user.permLevel = 2;
                    else if (user.can.view || user.can.view_this || user.can.view_job)
                        user.permLevel = 1;

                    return user;
                })
                .sort((a, b) => a.name.localeCompare(b.name));
        },
        subscriptions() {
            return useRepo(Subscription)
                .with('user')
                .where('subable_type', this.target.constructor.entity)
                .where('subable_id', this.target.id)
                .get();
        },
        fields() {
            return Object.assign({
                title: this.$t('subscriptions.title_subject', {subject: this.resourceTitle}),
                description: this.$t('subscriptions.description_subject', {subject: this.resourceTitle}),
                subscribed_title: this.$t('subscriptions.subscribed_users'),
                unsubscribe: this.$t('subscriptions.unsubscribe'),
                unsubscribed_title: this.$t('subscriptions.unsubscribed_users'),
                subscribe: this.$t('subscriptions.subscribe'),
                done: this.$t('subscriptions.ready')
            }, this.translations)
        },
        isJob() {
            return this.target instanceof Job
        },
    },
    methods: {
        getSegmentedButtonValue(user) {
            if (user.permLevel === 2 || this.isUserResponsible(user)) return 2;
            else if (user.permLevel === 1 && !this.isUserResponsible(user)) return 1;
            else return 0;
        },
        getSegmentedButtonOptions(user) {
            return [
                {
                    value: 2,
                    label: this.$t('permissions.options.read_and_manage'),
                    disabled: this.disableOptions(user, 'manage') || this.disableOptions(user, 'manage_job'),
                    classes: (this.getSegmentedButtonValue(user) === 2 ?
                        this.segmentedButtonColors.active[2] :
                        this.segmentedButtonColors.inactive) + ' ' +
                        (this.disableOptions(user, 'manage') || this.disableOptions(user, 'manage_job') ?
                            this.segmentedButtonColors.disabled :
                            'cursor-pointer hover:text-black hover:bg-primary-20 hover:border-primary-20'
                        ),
                },
                {
                    value: 1,
                    label: this.$t('permissions.options.read_only'),
                    disabled: this.disableOptions(user, 'manage') || this.disableOptions(user, 'manage_job'),
                    classes: (this.getSegmentedButtonValue(user) === 1 ?
                        this.segmentedButtonColors.active[1] :
                        this.segmentedButtonColors.inactive) + ' ' +
                        (this.disableOptions(user, 'manage') || this.disableOptions(user, 'manage_job') ?
                            this.segmentedButtonColors.disabled :
                            'cursor-pointer hover:text-black hover:bg-primary-20 hover:border-primary-20'
                        ),
                },
                {
                    value: 0,
                    label: this.$t('permissions.options.no_access'),
                    disabled: this.disableOptions(user, 'view') || this.disableOptions(user, 'view_job'),
                    classes: (this.getSegmentedButtonValue(user) === 0 ?
                        this.segmentedButtonColors.active[0] :
                        this.segmentedButtonColors.inactive) + ' ' +
                        (this.disableOptions(user, 'view') || this.disableOptions(user, 'view_job') ?
                            this.segmentedButtonColors.disabled :
                            'cursor-pointer hover:text-black hover:bg-primary-20 hover:border-primary-20'
                        ),
                }
            ]
        },
        setLevel(user, level) {
            if (user.permLevel === level || this.isUserResponsible(user))
                return;

            let perms = user.permissions[this.currentCompanyId];

            if (level === 0 && !user.can.view) {
                perms = perms.filter(perm => ![`${this.permissionPrefix}.view.${this.target.id}`, `${this.permissionPrefix}.manage.${this.target.id}`].includes(perm))
            } else if (level === 1 && !user.can.manage) {
                perms.push(`${this.permissionPrefix}.view.${this.target.id}`);
                perms = perms.filter(perm => ![`${this.permissionPrefix}.manage.${this.target.id}`].includes(perm))
            } else {
                perms.push(`${this.permissionPrefix}.view.${this.target.id}`);
                perms.push(`${this.permissionPrefix}.manage.${this.target.id}`);
            }

            perms = _uniq(perms);

            let newPerms = user.permissions;
            newPerms[this.currentCompanyId] = perms;
            useRepo(User).save({id: user.id, permissions: newPerms})

            permissions.setLevel(this.permissionPrefix, this.target.id, user.id, level);
        },

        isSubscribed(userId) {
            return this.subscriptions.find(i => i.user_id === userId);
        },
        subscribeUser(user) {
            if (this.updatingNotificationsOf[user.id]) {
                return;
            }

            this.updatingNotificationsOf[user.id] = true;

            subscriptions.store({
                data: {
                    attributes: {},
                    relationships: {
                        user: {
                            data: {
                                type: 'user',
                                id: user.id,
                            }
                        },
                        subable: {
                            data: {
                                type: this.target.constructor.entity,
                                id: this.target.id,
                            }
                        }
                    }
                }
            }).then(result => {
                return useRepo(Subscription).save(normalizeJson(result));
            }).then(() => {
                this.updatingNotificationsOf[user.id] = false;
            })
        },
        unsubscribeUser(subscription) {
            if (this.updatingNotificationsOf[subscription.id]) {
                return;
            }

            this.updatingNotificationsOf[subscription.id] = true;

            subscriptions.destroy(subscription.id).then(() => {
                useRepo(Subscription).destroy(subscription.id);
            }).then(() => {
                this.updatingNotificationsOf[subscription.id] = false;
            });
        },
        updateResponsible(userId, value) {
            useRepo(Job).save({id: this.target.id, responsible_user_id: value ? userId : null})

            jobs.updateResponsibleUser(this.target.id, {
                data: {
                    attributes: {
                        responsible_user_id: value ? userId : null
                    }
                }
            })
        },
        isUserResponsible(user) {
            if (this.isJob) {
                return user.id === this.target.responsible_user_id
            } else {
                return this.target.stage?.job?.responsible_user_id === user.id;
            }
        },
        disableOptions(user, type) {
            return user.can[type] || this.isUserResponsible(user);
        }
    },
    mounted() {
        this.usersModuleStore.fetchUsers('subscriptions');
    }
}
</script>

<style scoped>
</style>
